import React from "react";
import { Col, Row, Container } from "reactstrap";
import { Link } from "react-router-dom";
import ImgLogo from "assets/images/logo.png";
import { useTranslation, withTranslation } from "react-i18next";
import "./styles.scss";
import { withRouter } from "react-router-dom/cjs/react-router-dom";
import { useSite } from "contexts/SiteContext";

// ── Social icon map ───────────────────────────────────────────────────────────
const SocialIcon = ({ platform }) => {
	const icons = {
		facebook: (
			<path d="M18 2h-3a5 5 0 00-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 011-1h3z" />
		),
		twitter: (
			<path d="M23 3a10.9 10.9 0 01-3.14 1.53 4.48 4.48 0 00-7.86 3v1A10.66 10.66 0 013 4s-4 9 5 13a11.64 11.64 0 01-7 2c9 5 20 0 20-11.5a4.5 4.5 0 00-.08-.83A7.72 7.72 0 0023 3z" />
		),
		instagram: (
			<>
				<rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
				<path d="M16 11.37A4 4 0 1112.63 8 4 4 0 0116 11.37z" />
				<line x1="17.5" y1="6.5" x2="17.51" y2="6.5" />
			</>
		),
		snapchat: (
			<path d="M12 2C8.5 2 6 4.5 6 8v1.5c-.8.3-1.5.5-2 .5-.3 0-.5.2-.5.5s.2.5.5.6c1 .3 1.5 1 2 2 .3.5.8.9 1.5.9.4 0 .8-.1 1.3-.3.8.8 2 1.3 3.2 1.3s2.4-.5 3.2-1.3c.5.2.9.3 1.3.3.7 0 1.2-.4 1.5-.9.5-1 1-1.7 2-2 .3-.1.5-.3.5-.6s-.2-.5-.5-.5c-.5 0-1.2-.2-2-.5V8c0-3.5-2.5-6-6-6z" />
		),
		whatsapp: (
			<>
				<path d="M21 11.5a8.38 8.38 0 01-.9 3.8 8.5 8.5 0 01-7.6 4.7 8.38 8.38 0 01-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 01-.9-3.8 8.5 8.5 0 014.7-7.6 8.38 8.38 0 013.8-.9h.5a8.48 8.48 0 018 8v.5z" />
			</>
		),
		tiktok: <path d="M9 12a4 4 0 104 4V4a5 5 0 005 5" />,
		linked_in: (
			<>
				<path d="M16 8a6 6 0 016 6v7h-4v-7a2 2 0 00-2-2 2 2 0 00-2 2v7h-4v-7a6 6 0 016-6z" />
				<rect x="2" y="9" width="4" height="12" />
				<circle cx="4" cy="4" r="2" />
			</>
		),
		telegram: <path d="M21.5 2.5l-19 7.5 6 2.5 2 6.5 3.5-3.5 4.5 3.5 3-16z" />,
		you_tube: (
			<>
				<path d="M22.54 6.42a2.78 2.78 0 00-1.94-1.96C18.88 4 12 4 12 4s-6.88 0-8.6.46A2.78 2.78 0 001.46 6.42 29 29 0 001 12a29 29 0 00.46 5.58A2.78 2.78 0 003.4 19.54C5.12 20 12 20 12 20s6.88 0 8.6-.46a2.78 2.78 0 001.94-1.96A29 29 0 0023 12a29 29 0 00-.46-5.58z" />
				<polygon points="9.75 15.02 15.5 12 9.75 8.98 9.75 15.02" />
			</>
		),
	};
	const path = icons?.[platform];
	if (!path) return null;
	return (
		<svg
			width="16"
			height="16"
			viewBox="0 0 24 24"
			fill="none"
			stroke="#99A1AF"
			strokeWidth="1.8"
			strokeLinecap="round"
			strokeLinejoin="round">
			{path}
		</svg>
	);
};

const SOCIAL_ORDER = [
	"facebook",
	"instagram",
	"twitter",
	"tiktok",
	"snapchat",
	"whatsapp",
	"you_tube",
	"linked_in",
	"telegram",
];

function Footer() {
	const { t } = useTranslation();
	const { social, settings } = useSite();

	const activeSocials = SOCIAL_ORDER?.filter((key) => !!social?.[key]);

	return (
		<footer className="footer-home">
			<Container fluid>
				<Row className="footer-container pb-3 px-3 pt-1">
					{/* ── Brand col ───────────────────────────────────────────── */}
					<Col md={3} sm={12} className="mb-4">
						<img
							src={ImgLogo}
							width={64}
							height={64}
							className="img-fluid"
							alt="logo-icon"
						/>
						<p className="footer-description">
							{settings?.studio_name
								? `${settings.studio_name} — ${t("footer description")}`
								: t("footer description")}
						</p>

						{/* Social icons — only rendered when URL exists */}
						{activeSocials.length > 0 && (
							<div className="d-flex flex-wrap" style={{ gap: "10px" }}>
								{activeSocials.map((key) => {
									const label = key.replace(/_/g, " ");
									return (
										<a
											key={key}
											href={social[key]}
											target="_blank"
											rel="noopener noreferrer"
											aria-label={`${label} (opens in new tab)`}
											title={label}
											className="footer-social-icon">
											<SocialIcon platform={key} />
										</a>
									);
								})}
							</div>
						)}
					</Col>

					{/* ── Quick links ─────────────────────────────────────────── */}
					<Col md={3} sm={6} xs={6}>
						<div className="linksFooter">
							<h6 className="footer-title">{t("Quick Links")}</h6>
							<ul>
								<li>
									<Link to="/games">{t("Browse Games")}</Link>
								</li>
								<li>
									<Link to="/my-bookings">{t("My Bookings")}</Link>
								</li>
								<li>
									<Link to="/my-wallet">{t("Wallet")}</Link>
								</li>
								<li>
									<Link to="/profile">{t("Profile")}</Link>
								</li>
							</ul>
						</div>
					</Col>

					{/* ── Support links ───────────────────────────────────────── */}
					<Col md={3} sm={6} xs={6}>
						<div className="linksFooter">
							<h6 className="footer-title">{t("support")}</h6>
							<ul>
								<li>
									<Link to="/help-center">{t("Help Center")}</Link>
								</li>
								<li>
									<Link to="/faqs">{t("FAQs")}</Link>
								</li>
								<li>
									<Link to="/booking-policy">{t("Booking Policy")}</Link>
								</li>
								<li>
									<Link to="/cancellation">{t("Cancellation")}</Link>
								</li>
								<li>
									<Link to="/terms">{t("Terms & Conditions")}</Link>
								</li>
							</ul>
						</div>
					</Col>

					{/* ── Contact ─────────────────────────────────────────────── */}
					<Col md={3} sm={12}>
						<div className="linksFooter">
							<h6 className="footer-title">{t("Contact Us")}</h6>
							<div>
								{settings?.email && (
									<a
										href={`mailto:${settings.email}`}
										className="footer-contact-row">
										<div className="footer-contact-icon">
											<svg
												width="14"
												height="14"
												viewBox="0 0 24 24"
												fill="none"
												stroke="currentColor"
												strokeWidth="2"
												strokeLinecap="round"
												strokeLinejoin="round">
												<path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
												<polyline points="22,6 12,13 2,6" />
											</svg>
										</div>
										<span>{settings.email}</span>
									</a>
								)}

								{settings?.phone && (
									<a
										href={`tel:${settings.phone}`}
										className="footer-contact-row">
										<div className="footer-contact-icon">
											<svg
												width="14"
												height="14"
												viewBox="0 0 24 24"
												fill="none"
												stroke="currentColor"
												strokeWidth="2"
												strokeLinecap="round"
												strokeLinejoin="round">
												<path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 9.81 19.79 19.79 0 012 2.18h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L6.91 9.91a16 16 0 006.18 6.18l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z" />
											</svg>
										</div>
										<span>{settings.phone}</span>
									</a>
								)}

								{settings?.address && (
									<div className="footer-contact-row">
										<div className="footer-contact-icon">
											<svg
												width="14"
												height="14"
												viewBox="0 0 24 24"
												fill="none"
												stroke="currentColor"
												strokeWidth="2"
												strokeLinecap="round"
												strokeLinejoin="round">
												<path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z" />
												<circle cx="12" cy="10" r="3" />
											</svg>
										</div>
										<span>{settings.address}</span>
									</div>
								)}

								{settings?.opening_time && settings?.closing_time && (
									<div className="footer-contact-row">
										<div className="footer-contact-icon">
											<svg
												width="14"
												height="14"
												viewBox="0 0 24 24"
												fill="none"
												stroke="currentColor"
												strokeWidth="2"
												strokeLinecap="round"
												strokeLinejoin="round">
												<circle cx="12" cy="12" r="10" />
												<polyline points="12 6 12 12 16 14" />
											</svg>
										</div>
										<span>
											{settings.opening_time} – {settings.closing_time}
										</span>
									</div>
								)}
							</div>
						</div>
					</Col>
				</Row>

				{/* ── Copyright ───────────────────────────────────────────────── */}
				<div className="copyright">
					<p className="mb-0">
						©{new Date().getFullYear()}{" "}
						{settings?.studio_name || "VR Premium Studio"}. {t("All rights reserved.")}
					</p>
					<div className="d-flex" style={{ gap: "24px" }}>
						<Link to="/privacy-policy">{t("Privacy Policy")}</Link>
						<Link to="/cookie-policy">{t("Cookie Policy")}</Link>
						<Link to="/sitemap">{t("Sitemap")}</Link>
					</div>
				</div>
			</Container>
		</footer>
	);
}

export default withRouter(withTranslation()(Footer));
